import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../context/AuthContext';
import {
  prepareSocioEconomic,
  profileDetailsColumns,
  socioEconomicColumns
} from '../../services/dataService';
import { SocioEconomicData } from '../../types/domain';
import {
  SocioEconomicFields,
  emptySocioEconomic
} from '../components/SocioEconomicFields';
import {
  ProfileFields,
  ProfileFormValue,
  emptyProfileForm,
  resolveProfileForm
} from '../components/ProfileFields';

const CompleteProfile: React.FC = () => {
  const { user, refreshProfile } = useAuth();
  const [profileForm, setProfileForm] = useState<ProfileFormValue>(emptyProfileForm);
  const [socio, setSocio] = useState<SocioEconomicData>(emptySocioEconomic);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      // Validates the FR-02 fields, then turns the hospital picker's choice
      // into a control_places row before saving, so the profile references a
      // hospital by id rather than by free text.
      const { details, place, doctor } = await resolveProfileForm(profileForm);

      // FR-03 is "data tetap" — filled once, here. It rides along in the same
      // upsert rather than a follow-up write, so a failure halfway cannot
      // leave a profile that exists but has no family data and no way back to
      // this form.
      const socioColumns = socioEconomicColumns(prepareSocioEconomic(socio));

      const { error: profileError } = await supabase
        .from('profiles')
        .upsert([
          {
            id: user.id,
            ...profileDetailsColumns(details, place, doctor),
            ...socioColumns,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          },
        ]);

      if (profileError) {
        throw profileError;
      }

      await refreshProfile();
      navigate('/');
    } catch (err: any) {
      setError(err.message || 'Gagal menyimpan profil.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="screen">
      <div className="screen-card">
        <div className="section-title">Lengkapi Profil</div>
        <div className="subtitle">
          Silakan lengkapi data diri Anda sebelum melanjutkan.
        </div>

        <form onSubmit={handleSave}>
          <ProfileFields
            value={profileForm}
            onChange={setProfileForm}
            disabled={loading}
            idPrefix="onboarding-profile"
          />

          <div className="subtitle" style={{ marginTop: 20 }}>
            Data keluarga — cukup diisi sekali, bisa diubah nanti lewat menu
            "Profil".
          </div>
          <SocioEconomicFields
            value={socio}
            onChange={setSocio}
            disabled={loading}
            idPrefix="onboarding"
          />

          {error && (
            <div className="subtitle" style={{ color: '#b91c1c', marginTop: 16 }}>
              {error}
            </div>
          )}

          <div style={{ marginTop: 24 }}>
            <button
              type="submit"
              className="btn-primary w-full"
              disabled={loading}
            >
              {loading ? 'Menyimpan...' : 'Simpan Profil'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CompleteProfile;
